import React, { useState, useEffect } from 'react';
import { useKpros } from '../../context/KprosContext';
import { useTheme } from '../../context/ThemeContext';
import { X, Bookmark, Loader2, Sparkles } from 'lucide-react';
import { generateChatTitle } from '../../utils/titleGenerator';
import toast from 'react-hot-toast';

const FEATURE_OPTIONS = [
  { value: 'chat', label: 'Chat' },
  { value: 'studio', label: 'Studio' },
  { value: 'ait', label: 'AIT' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'profitability', label: 'Analyzer' },
  { value: 'team_chat', label: 'Team Chat' },
  { value: 'email', label: 'Email' },
];

export default function SavePromptDialog({ isOpen, onClose, content, featureTag }) {
  const { savePrompt } = useKpros();
  const { theme } = useTheme();
  const [title, setTitle] = useState('');
  const [feature, setFeature] = useState(featureTag || 'chat');
  const [saving, setSaving] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);

  useEffect(() => {
    if (isOpen && content) {
      setTitle('');
      setFeature(featureTag || 'chat');
      // Auto-generate a suggested title
      setGeneratingTitle(true);
      generateChatTitle({ userMessage: content, aiResponse: '', chatType: 'standard' })
        .then(suggested => {
          setTitle(suggested || '');
        })
        .catch(() => {})
        .finally(() => setGeneratingTitle(false));
    }
  }, [isOpen, content, featureTag]);

  if (!isOpen) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    const success = await savePrompt({
      title: title.trim(),
      content,
      feature_tag: feature,
    });
    setSaving(false);
    if (success) {
      toast.success('Prompt saved');
      onClose();
    } else {
      toast.error('Failed to save prompt');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-[70]" onClick={onClose} />
      <div className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] sm:w-[400px] z-[71] rounded-xl shadow-2xl font-rasa ${
        theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        <form onSubmit={handleSave}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Bookmark size={16} className="text-orange-500" />
              <h3 className="text-sm font-semibold font-unna text-gray-800 dark:text-gray-200">
                Save Prompt
              </h3>
            </div>
            <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            {/* Preview */}
            <div className={`p-2.5 rounded-lg text-xs leading-relaxed max-h-24 overflow-y-auto whitespace-pre-wrap ${
              theme === 'dark' ? 'bg-gray-750 text-gray-300 border border-gray-700' : 'bg-gray-50 text-gray-600 border border-gray-100'
            }`}>
              {content}
            </div>

            {/* Title */}
            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Title
                {generatingTitle && (
                  <span className="flex items-center gap-1 text-blue-500">
                    <Sparkles size={10} className="animate-pulse" />
                    <span className="text-[10px]">suggesting...</span>
                  </span>
                )}
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className={`w-full text-sm px-3 py-2 rounded-lg border ${
                  theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-800'
                } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                placeholder={generatingTitle ? 'Generating title...' : 'Give this prompt a name...'}
                maxLength={200}
                required
                autoFocus
              />
            </div>

            {/* Feature tag */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
              <select
                value={feature}
                onChange={e => setFeature(e.target.value)}
                className={`w-full text-sm px-3 py-2 rounded-lg border ${
                  theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-800'
                } focus:outline-none focus:ring-1 focus:ring-blue-500`}
              >
                {FEATURE_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Bookmark size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
